import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import { SuperAdminLayout } from '@/components/layout/SuperAdminLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { OverviewPage } from '@/pages/super-admin/Overview';
import { TenantsPage } from '@/pages/super-admin/Tenants';
import { TenantDetailPage } from '@/pages/super-admin/TenantDetail';
import { ProvisionWizardPage } from '@/pages/super-admin/ProvisionWizard';
import { ProvisionSuccessPage } from '@/pages/super-admin/ProvisionSuccess';
import { FeatureGatingPage } from '@/pages/super-admin/FeatureGating';
import { BusinessLeadsPage } from '@/pages/super-admin/BusinessLeads';
import { PayoutsPage } from '@/pages/super-admin/Payouts';
import { SupportPage } from '@/pages/super-admin/Support';
import { SuperAdminAnalyticsPage } from '@/pages/super-admin/Analytics';
import { AuditLogPage } from '@/pages/super-admin/AuditLog';
import { TenantHealthPage } from '@/pages/super-admin/TenantHealth';
import { PlatformAnnouncementsPage } from '@/pages/super-admin/PlatformAnnouncements';
import { DiscoveryInterviewsPage } from '@/pages/super-admin/DiscoveryInterviews';
import { SurveyPage } from '@/pages/SurveyPage';
import { EmailSettingsPage } from '@/pages/super-admin/EmailSettings';
import { AISettingsPage } from '@/pages/super-admin/AISettings';
import { SecretsReferencePage } from '@/pages/super-admin/SecretsReference';
import { InfrastructurePage } from '@/pages/super-admin/Infrastructure';
import { RunwayPage } from '@/pages/super-admin/Runway';
import { AppInstallsPage } from '@/pages/super-admin/AppInstalls';
import { BuddyAbusePage } from '@/pages/super-admin/BuddyAbuse';
import { InfrastructurePricingPage } from '@/pages/super-admin/InfrastructurePricing';
import { PlanBuilder } from '@/pages/super-admin/PlanBuilder';
import RetentionDefaultsPage from '@/pages/super-admin/RetentionDefaults';
import { RunbookPage } from '@/pages/super-admin/RunbookPage';
import SuperAdminFeedbackPage from '@/pages/super-admin/Feedback';
import LogoPreviewPage from '@/pages/super-admin/LogoPreview';
import { TenantManagePage } from '@/pages/tenant-admin/TenantManage';
import { PlatformPlansPage } from '@/pages/shared/PlatformPlans';
import { LoginPage } from '@/pages/auth/Login';
import { SignupPage } from '@/pages/auth/Signup';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPassword';
import { ResetPasswordPage } from '@/pages/auth/ResetPassword';
import { AskMeWidget } from '@/components/help/AskMeWidget';
import TestEnvPill from '@/components/dev/TestEnvPill';

/** Guards super-admin-only routes — redirects tenant admins to their manage page */
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin, myTenants, loading } = useAuth();
  if (loading) return null;
  if (!isSuperAdmin) {
    // Tenant admin tried to access super admin page → send to their manage page
    if (myTenants.length >= 1) {
      return <Navigate to={`/admin/tenants/${myTenants[0].tenant.id}/manage`} replace />;
    }
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

function Admin({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <SuperAdminRoute>
        <SuperAdminLayout>{children}</SuperAdminLayout>
      </SuperAdminRoute>
    </ProtectedRoute>
  );
}

/** Route landing: super admins → Overview, tenant admins → manage page, others → no access */
function SmartLanding() {
  const { isSuperAdmin, myTenants, loading } = useAuth();

  if (loading) return null;

  // Super admin → render overview inside their layout
  if (isSuperAdmin) return <SuperAdminLayout><OverviewPage /></SuperAdminLayout>;

  // Tenant admin → redirect straight to their manage page (no super admin sidebar)
  if (myTenants.length >= 1) {
    return <Navigate to={`/admin/tenants/${myTenants[0].tenant.id}/manage`} replace />;
  }

  // No tenants, not super admin → clean minimal page (no sidebar)
  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center text-center">
      <div>
        <h2 className="text-xl font-semibold text-surface-900 mb-2">No Access</h2>
        <p className="text-sm text-surface-500">You don't have access to any organisations yet.<br />Contact your administrator.</p>
      </div>
    </div>
  );
}

function ThemedToaster() {
  const { darkMode } = useTheme();
  return (
    <Toaster position="top-right" theme={darkMode ? 'dark' : 'light'}
      toastOptions={{ style: darkMode
        ? { background: '#16202e', border: '1px solid rgba(255,255,255,0.06)', color: '#f1f5f9' }
        : { background: '#FFFFFF', border: '1px solid #E4E3DF', color: '#1C1B18' }
      }} />
  );
}

export default function App() {
  // The public survey (participant-facing) must not show admin chrome.
  const { pathname } = useLocation();
  const publicPage = pathname.startsWith('/survey');
  return (
    <ThemeProvider>
      <ThemedToaster />
      {!publicPage && <AskMeWidget />}
      {!publicPage && <TestEnvPill />}

      <Routes>
        {/* ── Public discovery survey (no auth) ────────────── */}
        <Route path="/survey" element={<SurveyPage />} />

        {/* ── Auth (public) ────────────────────────────────── */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* ── Smart landing — role-based redirect (no sidebar until role is known) */}
        <Route path="/admin" element={<ProtectedRoute><SmartLanding /></ProtectedRoute>} />

        {/* ── Super Admin (protected + guarded) ──────────────── */}
        <Route path="/admin/tenants" element={<Admin><TenantsPage /></Admin>} />
        <Route path="/admin/tenants/new" element={<Admin><ProvisionWizardPage /></Admin>} />
        <Route path="/admin/tenants/:tenantId" element={<Admin><TenantDetailPage /></Admin>} />
        <Route path="/admin/tenants/:tenantId/provision-success" element={<Admin><ProvisionSuccessPage /></Admin>} />
        <Route path="/admin/business-leads" element={<Admin><BusinessLeadsPage /></Admin>} />
        <Route path="/admin/feature-gating" element={<Admin><FeatureGatingPage /></Admin>} />
        <Route path="/admin/retention-defaults" element={<Admin><RetentionDefaultsPage /></Admin>} />
        <Route path="/admin/runbook" element={<Admin><RunbookPage /></Admin>} />
        <Route path="/admin/feedback" element={<Admin><SuperAdminFeedbackPage /></Admin>} />
        <Route path="/admin/logo-preview" element={<Admin><LogoPreviewPage /></Admin>} />
        <Route path="/admin/plan-builder" element={<Admin><PlanBuilder /></Admin>} />
        <Route path="/admin/platform-plans" element={<Admin><PlatformPlansPage /></Admin>} />
        <Route path="/admin/payouts" element={<Admin><PayoutsPage /></Admin>} />
        <Route path="/admin/audit-log" element={<Admin><AuditLogPage /></Admin>} />
        <Route path="/admin/tenant-health" element={<Admin><TenantHealthPage /></Admin>} />
        <Route path="/admin/buddy-abuse" element={<Admin><BuddyAbusePage /></Admin>} />
        <Route path="/admin/announcements" element={<Admin><PlatformAnnouncementsPage /></Admin>} />
        <Route path="/admin/interviews" element={<Admin><DiscoveryInterviewsPage /></Admin>} />
        <Route path="/admin/email-settings" element={<Admin><EmailSettingsPage /></Admin>} />
        <Route path="/admin/ai-settings"    element={<Admin><AISettingsPage    /></Admin>} />
        <Route path="/admin/secrets-reference" element={<Admin><SecretsReferencePage /></Admin>} />
        <Route path="/admin/infrastructure" element={<Admin><InfrastructurePage /></Admin>} />
        <Route path="/admin/runway" element={<Admin><RunwayPage /></Admin>} />
        <Route path="/admin/app-installs" element={<Admin><AppInstallsPage /></Admin>} />
        <Route path="/admin/infrastructure-pricing" element={<Admin><InfrastructurePricingPage /></Admin>} />
        {/* /admin/edge-functions was the standalone catalog page — merged
            into Infrastructure as its first tab. Keep a redirect so any
            bookmarks / direct links still land somewhere useful. */}
        <Route path="/admin/edge-functions" element={<Navigate to="/admin/infrastructure" replace />} />
        <Route path="/admin/support" element={<Admin><SupportPage /></Admin>} />
        <Route path="/admin/analytics" element={<Admin><SuperAdminAnalyticsPage /></Admin>} />

        {/* ── Tenant Admin (protected, no super admin sidebar) ── */}
        <Route path="/admin/tenants/:tenantId/manage/*" element={<ProtectedRoute><TenantManagePage /></ProtectedRoute>} />

        {/* ── Catch-all — role-aware redirect ────────────────── */}
        <Route path="*" element={<ProtectedRoute><SmartLanding /></ProtectedRoute>} />
      </Routes>
    </ThemeProvider>
  );
}
